import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Key, Lock } from "lucide-react";
import Button from "components/shared/Button";
import Dialog from "components/shared/Dialog";
import { useProfile } from "../../hooks/useProfile";
import { useResetPasswordMutation } from "../../api/useResetPasswordMutation";
import { renderUserName } from "utils/helpers/user";

function ResetPasswordModal({ isOpen, close, user: userProp }: any) {
    const { t } = useTranslation();
    const { user: profileUser } = useProfile();
    const user = userProp ?? profileUser;
    const [plainPassword, setPlainPassword] = useState<string>();
    const { mutate: resetPassword, isPending } = useResetPasswordMutation();

    const onResetPassword = useCallback(() => {
        resetPassword(
            user?.id,
            { onSuccess: (response) => setPlainPassword(response) },
        );
    }, [resetPassword, user?.id]);

    const onClose = useCallback(() => {
        setPlainPassword(undefined);
        close();
    }, [close]);

    return (
        <Dialog
            open={isOpen}
            close={onClose}
            size="lg"
        >
            {!plainPassword ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-flex-col tw-mb-5">
                    <div className="tw-bg-orange-100 tw-p-4 tw-rounded-full tw-text-orange-500 tw-my-3">
                        <Lock size={30} />
                    </div>

                    <h1 className="tw-font-semibold tw-text-lg">{t("reset-user-password")}</h1>

                    <p className="tw-text-sm tw-text-center">
                        <Trans
                            i18nKey="reset-password-description"
                            values={{ userName: renderUserName(user) }}
                            components={{ b: <strong className="tw-font-bold" /> }}
                        />
                    </p>

                    <p className="tw-mt-2 tw-text-xs tw-text-muted-foreground">{t("reset-password-warning")}</p>
                </div>
            ) : (
                <div className="tw-flex tw-items-center tw-justify-center tw-flex-col tw-mb-5">
                    <div className="tw-bg-primary/10 tw-p-4 tw-rounded-full tw-text-primary tw-my-3">
                        <Key size={30} />
                    </div>

                    <h1 className="tw-font-semibold tw-text-lg">{t("password-reset-successful")}</h1>

                    <p className="tw-text-sm tw-text-center">
                        {t("new-password-for")} <strong className="tw-font-bold">{renderUserName(user)}</strong>:
                    </p>

                    <p className="tw-w-full tw-rounded-lg tw-border tw-border-border tw-bg-muted tw-p-4 tw-my-2 tw-text-center tw-break-all tw-font-mono tw-text-lg tw-font-bold tw-text-foreground">
                        {plainPassword}
                    </p>

                    <p className="tw-text-xs tw-text-muted-foreground tw-text-center">{t("password-display-warning")}</p>
                </div>
            )}

            <div className="tw-flex tw-justify-end tw-items-center tw-gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                >
                    {plainPassword ? t("close") : t("cancel")}
                </Button>

                {!plainPassword && (
                    <Button
                        className="tw-bg-orange-100 tw-text-orange-500 hover:tw-bg-orange-200"
                        type="button"
                        loading={isPending}
                        onClick={onResetPassword}
                    >
                        {t("ok")}
                    </Button>
                )}
            </div>
        </Dialog>
    );
}

export default ResetPasswordModal;
